/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React from 'react';
import { View } from 'react-native';
import Svg, { Circle, Ellipse, G, Path, Text as SvgText } from 'react-native-svg';
import { ticketColors } from '../../shared/theme/public-api';

type Mood = 'thinking' | 'confused' | 'sleeping';

type Props = {
  mood: Mood;
  size?: number;
  testID?: string;
};

export function CatalogProductDetailStateMascot({ mood, size = 42, testID }: Props) {
  const sleeping = mood === 'sleeping';
  const confused = mood === 'confused';

  return (
    <View testID={testID} accessible={false} importantForAccessibility="no-hide-descendants">
      <Svg width={size} height={(size * 240) / 200} viewBox="0 0 200 240">
        <G transform="translate(100 120)">
          {sleeping ? (
            <>
              <Path d="M55 0 Q72 15 75 30" fill="none" stroke={ticketColors.brandPrimary} strokeWidth={7} strokeLinecap="round" />
              <Circle cx={77} cy={33} r={14} fill={ticketColors.crece} stroke={ticketColors.brandInk} strokeWidth={2} opacity={0.7} />
              <SvgText x={77} y={38} fontSize={13} fontWeight="700" fill={ticketColors.paper} textAnchor="middle">$</SvgText>
            </>
          ) : (
            <>
              <Path d="M55 -50 Q80 -65 88 -80" fill="none" stroke={ticketColors.brandPrimary} strokeWidth={7} strokeLinecap="round" />
              <Circle cx={90} cy={-84} r={16} fill={ticketColors.crece} stroke={ticketColors.brandInk} strokeWidth={2} />
              <SvgText x={90} y={-79} fontSize={15} fontWeight="700" fill={ticketColors.paper} textAnchor="middle">$</SvgText>
            </>
          )}
          {sleeping ? (
            <Ellipse cx={0} cy={0} rx={62} ry={68} fill={ticketColors.brandPrimary} transform="rotate(-5)" />
          ) : (
            <Ellipse cx={0} cy={0} rx={62} ry={68} fill={ticketColors.brandPrimary} />
          )}
          <Ellipse cx={-18} cy={-30} rx={22} ry={14} fill={ticketColors.brandDeep} opacity={sleeping ? 0.4 : 0.6} />
          {sleeping ? (
            <>
              <Path d="M-55 5 Q-72 25 -70 45" fill="none" stroke={ticketColors.brandPrimary} strokeWidth={7} strokeLinecap="round" />
              <Circle cx={-72} cy={48} r={9} fill={ticketColors.brandPrimary} />
            </>
          ) : confused ? (
            <>
              <Path d="M-55 -20 Q-65 -35 -55 -50" fill="none" stroke={ticketColors.brandPrimary} strokeWidth={7} strokeLinecap="round" />
              <Circle cx={-57} cy={-53} r={9} fill={ticketColors.brandPrimary} />
            </>
          ) : (
            <>
              <Path d="M-55 -10 Q-50 -30 -35 -35" fill="none" stroke={ticketColors.brandPrimary} strokeWidth={7} strokeLinecap="round" />
              <Circle cx={-32} cy={-36} r={9} fill={ticketColors.brandPrimary} />
            </>
          )}
          <Path d="M-20 65 Q-25 80 -22 95 M20 65 Q25 80 22 95" fill="none" stroke={ticketColors.brandPrimary} strokeWidth={7} strokeLinecap="round" />
          <Ellipse cx={-22} cy={98} rx={12} ry={6} fill={ticketColors.brandInk} />
          <Ellipse cx={22} cy={98} rx={12} ry={6} fill={ticketColors.brandInk} />
          {sleeping ? (
            <>
              <Path d="M-30 -15 Q-20 -10 -10 -15 M10 -15 Q20 -10 30 -15" fill="none" stroke={ticketColors.ink} strokeWidth={4} strokeLinecap="round" />
              <Ellipse cx={0} cy={18} rx={5} ry={7} fill={ticketColors.ink} />
              <SvgText x={45} y={-40} fontSize={14} fontWeight="700" fill={ticketColors.brandDeep} opacity={0.7}>z</SvgText>
              <SvgText x={55} y={-55} fontSize={18} fontWeight="700" fill={ticketColors.brandDeep} opacity={0.7}>z</SvgText>
              <SvgText x={68} y={-72} fontSize={22} fontWeight="700" fill={ticketColors.brandDeep} opacity={0.7}>Z</SvgText>
            </>
          ) : confused ? (
            <>
              <Circle cx={-20} cy={-18} r={11} fill={ticketColors.paper} />
              <Circle cx={20} cy={-12} r={11} fill={ticketColors.paper} />
              <Circle cx={-18} cy={-16} r={7} fill={ticketColors.ink} />
              <Circle cx={22} cy={-10} r={7} fill={ticketColors.ink} />
              <Circle cx={-15} cy={-19} r={2.5} fill={ticketColors.paper} />
              <Circle cx={25} cy={-13} r={2.5} fill={ticketColors.paper} />
              <Path d="M-30 -32 L-12 -35 M12 -28 L30 -25" fill="none" stroke={ticketColors.ink} strokeWidth={3} strokeLinecap="round" />
              <Path d="M-15 18 L-8 14 L0 18 L8 14 L15 18" fill="none" stroke={ticketColors.ink} strokeWidth={4} strokeLinecap="round" strokeLinejoin="round" />
              <SvgText x={40} y={-55} fontSize={22} fontWeight="700" fill={ticketColors.brandDeep} opacity={0.7}>?</SvgText>
            </>
          ) : (
            <>
              <Circle cx={-20} cy={-15} r={11} fill={ticketColors.paper} />
              <Circle cx={20} cy={-15} r={11} fill={ticketColors.paper} />
              <Circle cx={-18} cy={-20} r={7} fill={ticketColors.ink} />
              <Circle cx={22} cy={-20} r={7} fill={ticketColors.ink} />
              <Circle cx={-15} cy={-22} r={2.5} fill={ticketColors.paper} />
              <Circle cx={25} cy={-22} r={2.5} fill={ticketColors.paper} />
              <Path d="M-15 18 Q0 18 15 18 M-28 -30 L-14 -33" fill="none" stroke={ticketColors.ink} strokeWidth={3} strokeLinecap="round" />
              <Circle cx={-50} cy={-60} r={4} fill="none" stroke={ticketColors.brandDeep} strokeWidth={2} />
              <Circle cx={-58} cy={-72} r={6} fill="none" stroke={ticketColors.brandDeep} strokeWidth={2} />
              <Circle cx={-68} cy={-88} r={9} fill="none" stroke={ticketColors.brandDeep} strokeWidth={2} />
            </>
          )}
        </G>
      </Svg>
    </View>
  );
}
